import { useAprendicesOptions } from "@/hooks/aprendices/useAprendices";
import { AsyncPaginate } from "react-select-async-paginate";
import { useEffect, useState } from "react";
import { axiosInstance } from "@retro-ia/shared/axios";
import type { LoadOptions } from "react-select-async-paginate";
import type { GroupBase, StylesConfig } from "react-select";

interface AprendizOption {
  label: string;
  value: string;
}

interface SelectAprendizAsyncProps {
  value: AprendizOption | null | string;
  onChange: (option: AprendizOption | null) => void;
  disabled?: boolean;
  placeholder?: string;
}

export function SelectAprendiz({
  value,
  onChange,
  disabled,
  placeholder = "Selecciona un aprendiz",
}: SelectAprendizAsyncProps) {
  const [selected, setSelected] = useState<AprendizOption | null>(
    value && typeof value === "object" ? (value as AprendizOption) : null
  );
  const [inputValue, setInputValue] = useState("");
  const [page, setPage] = useState(1);

  // Hook para obtener las opciones
  const { data } = useAprendicesOptions(inputValue, page, 20);

  useEffect(() => {
    if (value && typeof value === "object" && "label" in value) {
      if (!selected || selected.value !== value.value) {
        setSelected(value as AprendizOption);
      }
      return;
    }
    if (value && (typeof value === "string" || typeof value === "number")) {
      const id = String(value);
      if (!id || id.trim() === "") {
        setSelected(null);
        return;
      }
      (async () => {
        try {
          const { data } = await axiosInstance.get(`/aprendices/${id}`);
          setSelected({
            value: String(data.id),
            label:
              (data.usuario?.nombre ?? "") +
              (data.usuario?.apellidoPaterno
                ? ` ${data.usuario.apellidoPaterno}`
                : "") +
              (data.usuario?.apellidoMaterno
                ? ` ${data.usuario.apellidoMaterno}`
                : "") +
              (data.usuario?.nombreUsuario
                ? ` (${data.usuario.nombreUsuario})`
                : ""),
          });
        } catch {
          setSelected(null);
        }
      })();
      return;
    }
  }, [value, selected]);

  const loadOptions: LoadOptions<
    AprendizOption,
    GroupBase<AprendizOption>,
    { page: number }
  > = async (search, loadedOptions, additional) => {
    const nextPage = additional?.page ?? 1;
    setInputValue(search);
    setPage(nextPage);
    const options = (data?.data ?? []).map((a) => ({
      label: a.nombre,
      value: String(a.id),
    }));
    return {
      options,
      hasMore: loadedOptions.length + options.length < (data?.total ?? 0),
      additional: { page: nextPage + 1 },
    };
  };

  const customStyles: StylesConfig<AprendizOption, false> = {
    control: (base, state) => ({
      ...base,
      backgroundColor: "var(--background)",
      borderColor: state.isFocused ? "var(--ring)" : "var(--border)",
      boxShadow: state.isFocused ? "0 0 0 2px var(--ring)" : "none",
      borderRadius: "0.5rem",
      minHeight: "2.5rem",
      padding: "0 0.25rem",
      color: "var(--foreground)",
      transition: "border-color 0.2s, box-shadow 0.2s",
    }),
    placeholder: (base) => ({
      ...base,
      color: "var(--foreground)",
    }),
    singleValue: (base) => ({
      ...base,
      color: "var(--foreground)",
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: "var(--background)",
      border: "1px solid var(--muted)",
      borderRadius: "0.5rem",
      boxShadow: "0 4px 8px rgba(0,0,0,0.08)",
      zIndex: 20,
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isFocused ? "var(--primary)" : "var(--background)",
      color: state.isFocused ? "var(--muted)" : "var(--foreground)",
      padding: "0.5rem 0.75rem",
      cursor: "pointer",
    }),
    input: (base) => ({
      ...base,
      color: "var(--foreground)",
    }),
    dropdownIndicator: (base) => ({
      ...base,
      color: "var(--muted-foreground)",
    }),
  };

  return (
    <AsyncPaginate
      value={selected}
      loadOptions={loadOptions}
      onChange={(option) => {
        setSelected(option);
        onChange(option ? option : null);
      }}
      isDisabled={disabled}
      placeholder={placeholder}
      additional={{ page: 1 }}
      debounceTimeout={300}
      styles={customStyles}
    />
  );
}
